import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { courseApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, Spinner, Modal } from '../../components/shared/index';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  TrashIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  ClockIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

const CONTENT_TYPES = [
  { value: 'video',         label: 'Video' },
  { value: 'article',       label: 'Article' },
  { value: 'quiz',          label: 'Quiz' },
  { value: 'scorm',         label: 'SCORM' },
  { value: 'external_link', label: 'External link' },
];

const TYPE_COLORS = {
  video:         'bg-red-50   text-red-700',
  article:       'bg-blue-50  text-blue-700',
  quiz:          'bg-purple-50 text-purple-700',
  scorm:         'bg-amber-50 text-amber-700',
  external_link: 'bg-gray-100 text-gray-700',
};

function CourseForm({ initial, onSubmit, onCancel, saving }) {
  const [form, setForm] = useState({
    title:               initial?.title               || '',
    description:         initial?.description         || '',
    content_type:        initial?.content_type        || 'external_link',
    content_url:         initial?.content_url         || '',
    duration_minutes:    initial?.duration_minutes    || '',
    competency_tags:     (initial?.competency_tags    || []).join(', '),
    is_mandatory:        initial?.is_mandatory        || false,
    mandatory_for_roles: (initial?.mandatory_for_roles || []).join(', '),
    is_active:           initial?.is_active ?? true,
  });

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      ...form,
      duration_minutes:    form.duration_minutes ? Number(form.duration_minutes) : null,
      competency_tags:     form.competency_tags.split(',').map(s => s.trim()).filter(Boolean),
      mandatory_for_roles: form.mandatory_for_roles.split(',').map(s => s.trim()).filter(Boolean),
    });
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="label">Title</label>
        <input required className="input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
      </div>
      <div>
        <label className="label">Description</label>
        <textarea className="input h-20 resize-none" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">Content type</label>
          <select className="input" value={form.content_type} onChange={e => setForm({ ...form, content_type: e.target.value })}>
            {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Duration (minutes)</label>
          <input type="number" min="0" className="input" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: e.target.value })} />
        </div>
      </div>
      <div>
        <label className="label">Content URL</label>
        <input className="input" value={form.content_url} onChange={e => setForm({ ...form, content_url: e.target.value })} placeholder="https://…" />
      </div>
      <div>
        <label className="label">Competency tags (comma-separated)</label>
        <input className="input" value={form.competency_tags} onChange={e => setForm({ ...form, competency_tags: e.target.value })} placeholder="leadership, python, communication" />
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.is_mandatory} onChange={e => setForm({ ...form, is_mandatory: e.target.checked })} />
          Mandatory training
        </label>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.is_active} onChange={e => setForm({ ...form, is_active: e.target.checked })} />
          Active
        </label>
      </div>
      {form.is_mandatory && (
        <div>
          <label className="label">Mandatory for roles (comma-separated, blank = all roles)</label>
          <input className="input" value={form.mandatory_for_roles} onChange={e => setForm({ ...form, mandatory_for_roles: e.target.value })} placeholder="employee, manager" />
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className="btn-secondary">Cancel</button>
        <button type="submit" disabled={saving} className="btn-primary">{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </form>
  );
}

function CourseDetailModal({ course, enrolled, onEnroll, onClose, enrolling }) {
  if (!course) return null;
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-xs">
        <span className={clsx('px-2 py-0.5 rounded capitalize', TYPE_COLORS[course.content_type] || TYPE_COLORS.external_link)}>
          {course.content_type?.replace('_',' ')}
        </span>
        {course.duration_minutes && <span className="text-gray-500 flex items-center gap-1"><ClockIcon className="w-3.5 h-3.5" />{course.duration_minutes} min</span>}
        {course.is_mandatory && (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-red-50 text-red-700">
            <ShieldCheckIcon className="w-3.5 h-3.5" />Mandatory
          </span>
        )}
      </div>
      <p className="text-sm text-gray-700 whitespace-pre-line">{course.description || 'No description.'}</p>
      {(course.competency_tags || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-1.5">Competencies</p>
          <div className="flex flex-wrap gap-1.5">
            {course.competency_tags.map(t => (
              <span key={t} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">{t}</span>
            ))}
          </div>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <button onClick={onClose} className="btn-secondary">Close</button>
        {course.content_url && (
          <a href={course.content_url} target="_blank" rel="noopener noreferrer" className="btn-secondary">
            Preview <ArrowTopRightOnSquareIcon className="w-4 h-4" />
          </a>
        )}
        {enrolled ? (
          <span className="btn-secondary opacity-80 cursor-default">Already enrolled</span>
        ) : (
          <button onClick={onEnroll} disabled={enrolling} className="btn-primary">
            {enrolling ? 'Enrolling…' : 'Enrol'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function CourseCatalog() {
  const { user } = useAuth();
  const isHr     = ['hr_admin','super_admin'].includes(user?.role);
  const qc       = useQueryClient();

  const [search, setSearch]         = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [onlyMand, setOnlyMand]     = useState(false);
  const [open, setOpen]             = useState(null);     // { course, mode: 'view' | 'edit' | 'create' }

  const { data: courses = [], isLoading } = useQuery({
    queryKey: ['courses'],
    queryFn:  () => courseApi.list({ include_inactive: isHr ? 1 : undefined }).then(r => r.data),
  });

  const { data: myEnr = [] } = useQuery({
    queryKey: ['my-enrollments'],
    queryFn:  () => courseApi.mine().then(r => r.data),
  });
  const enrolledIds = new Set(myEnr.map(e => e.course_id));

  const createMut = useMutation({
    mutationFn: courseApi.create,
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['courses'] }); setOpen(null); toast.success('Course created'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to create course'),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, data }) => courseApi.update(id, data),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['courses'] }); setOpen(null); toast.success('Course updated'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to update course'),
  });
  const deleteMut = useMutation({
    mutationFn: courseApi.delete,
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['courses'] }); toast.success('Archived'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to archive'),
  });
  const enrollMut = useMutation({
    mutationFn: (id) => courseApi.enroll(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['my-enrollments'] }); setOpen(null); toast.success('Enrolled'); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed to enrol'),
  });

  const filtered = useMemo(() => {
    return courses.filter(c => {
      if (search && !(c.title + ' ' + (c.description || '')).toLowerCase().includes(search.toLowerCase())) return false;
      if (typeFilter && c.content_type !== typeFilter) return false;
      if (onlyMand && !c.is_mandatory) return false;
      return true;
    });
  }, [courses, search, typeFilter, onlyMand]);

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;

  return (
    <div>
      <div className="flex flex-wrap gap-2 items-center mb-4">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="input pl-9" placeholder="Search courses…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="input w-44" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {CONTENT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-700 whitespace-nowrap px-2">
          <input type="checkbox" checked={onlyMand} onChange={e => setOnlyMand(e.target.checked)} />
          Mandatory only
        </label>
        {isHr && (
          <button onClick={() => setOpen({ mode: 'create' })} className="btn-primary ml-auto">
            <PlusIcon className="w-4 h-4" />New course
          </button>
        )}
      </div>

      {filtered.length === 0 ? (
        <div className="card">
          <EmptyState
            title="No courses match"
            description={isHr ? 'Create your first course to start building your library.' : 'Try adjusting your filters.'}
            action={isHr && <button onClick={() => setOpen({ mode: 'create' })} className="btn-primary"><PlusIcon className="w-4 h-4" />New course</button>}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(c => {
            const enrolled = enrolledIds.has(c.id);
            return (
              <div key={c.id} className={clsx('card p-4 flex flex-col', !c.is_active && 'opacity-60')}>
                <div className="flex items-start justify-between mb-2">
                  <button
                    onClick={() => setOpen({ course: c, mode: 'view' })}
                    className="text-sm font-semibold text-gray-900 hover:text-blue-600 text-left line-clamp-2"
                  >
                    {c.title}
                  </button>
                  {c.is_mandatory && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-red-50 text-red-700 text-[10px] font-semibold shrink-0">
                      <ShieldCheckIcon className="w-3 h-3" />MANDATORY
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 flex-1 line-clamp-3 mb-3">{c.description}</p>
                <div className="flex items-center gap-2 text-xs mb-3">
                  <span className={clsx('px-2 py-0.5 rounded capitalize', TYPE_COLORS[c.content_type] || TYPE_COLORS.external_link)}>
                    {c.content_type?.replace('_',' ')}
                  </span>
                  {c.duration_minutes && <span className="text-gray-500">{c.duration_minutes} min</span>}
                </div>
                {(c.competency_tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {c.competency_tags.slice(0,3).map(t => (
                      <span key={t} className="px-1.5 py-0.5 bg-blue-50 text-blue-700 text-[10px] rounded">{t}</span>
                    ))}
                    {c.competency_tags.length > 3 && <span className="text-[10px] text-gray-400">+{c.competency_tags.length - 3}</span>}
                  </div>
                )}
                <div className="flex gap-2 mt-auto">
                  {enrolled ? (
                    <span className="btn-secondary btn-sm flex-1 justify-center opacity-70 cursor-default">Enrolled</span>
                  ) : (
                    <button
                      onClick={() => enrollMut.mutate(c.id)}
                      disabled={enrollMut.isPending}
                      className="btn-primary btn-sm flex-1 justify-center"
                    >
                      Enrol
                    </button>
                  )}
                  {isHr && (
                    <>
                      <button onClick={() => setOpen({ course: c, mode: 'edit' })} className="btn-secondary btn-sm" title="Edit">
                        <PencilSquareIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => { if (confirm(`Archive "${c.title}"?`)) deleteMut.mutate(c.id); }}
                        className="btn-secondary btn-sm text-red-600"
                        title="Archive"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Modal
        open={open?.mode === 'view'}
        onClose={() => setOpen(null)}
        title={open?.course?.title || 'Course'}
        size="md"
      >
        <CourseDetailModal
          course={open?.course}
          enrolled={open?.course && enrolledIds.has(open.course.id)}
          onEnroll={() => open?.course && enrollMut.mutate(open.course.id)}
          onClose={() => setOpen(null)}
          enrolling={enrollMut.isPending}
        />
      </Modal>

      <Modal
        open={open?.mode === 'create' || open?.mode === 'edit'}
        onClose={() => setOpen(null)}
        title={open?.mode === 'edit' ? 'Edit course' : 'New course'}
        size="lg"
      >
        {(open?.mode === 'create' || open?.mode === 'edit') && (
          <CourseForm
            initial={open?.course}
            saving={createMut.isPending || updateMut.isPending}
            onCancel={() => setOpen(null)}
            onSubmit={(data) => {
              if (open.mode === 'edit') updateMut.mutate({ id: open.course.id, data });
              else createMut.mutate(data);
            }}
          />
        )}
      </Modal>
    </div>
  );
}
